/**
 * Minimal Cognito Hosted UI PKCE flow. No SDK dependency — keeps the bundle small.
 * Env vars (set at build via VITE_*):
 *  - VITE_COGNITO_DOMAIN      e.g. https://auth.future-demand.com
 *  - VITE_COGNITO_CLIENT_ID
 *  - VITE_COGNITO_REDIRECT    e.g. https://billing.future-demand.com/auth/callback
 *  - VITE_COGNITO_LOGOUT_REDIRECT
 */

import { isAdminView } from "./adminView";
import { getSupportStaffViewToken, clearSupportStaffView } from './supportStaffView';
import { isPortalSession, clearPortalSession } from "./portalSession";
import { hasCustomerSession, markCustomerSession, portalHeaders, safeReturnPath } from "./portalPlatform";
import { COGNITO_STORAGE_KEY, clearCognitoSession } from "./cognitoStorage";

const API_BASE = (import.meta.env.VITE_API_BASE as string) ?? "/api";

// Strip any trailing slash so `${DOMAIN}/oauth2/...` never yields a `//` path
// (a trailing slash in VITE_COGNITO_DOMAIN otherwise 404s every Hosted UI endpoint).
const DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN as string)?.replace(/\/+$/, "");
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
const REDIRECT = import.meta.env.VITE_COGNITO_REDIRECT as string;
const LOGOUT_REDIRECT = import.meta.env.VITE_COGNITO_LOGOUT_REDIRECT as string;

const STORAGE_KEY = COGNITO_STORAGE_KEY;
const PKCE_KEY = "fd_cognito_pkce";

interface Session {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_at: number;
}

function readSession(): Session | null {
  const raw = sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

function writeSession(s: Session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession() {
  clearCognitoSession();
}

export async function login(returnTo?: string, loginHint?: string): Promise<void> {
  // An explicit account sign-in starts a new authentication, not a mix of the
  // previous cookie identity and a new Cognito bearer.
  const ended = await fetch(`${API_BASE}/public/portal/logout`, { method: "POST", credentials: "include", headers: portalHeaders() });
  if (!ended.ok) throw new Error("Could not end the previous session.");
  markCustomerSession(false); clearPortalSession(); clearSession();
  const verifier = randomString(64);
  const challenge = await pkceChallenge(verifier);
  const state = randomString(32);
  sessionStorage.setItem(
    PKCE_KEY,
    JSON.stringify({ verifier, state, returnTo: safeReturnPath(returnTo ?? null) })
  );
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    // Only scopes enabled on the fd-billing-oidc app client. The customer flow needs
    // openid + email; `profile`/`phone` are not requested (we use sub/email only).
    scope: "openid email",
    redirect_uri: REDIRECT,
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
  });
  // Prefill the hosted-UI username when we already know the email (e.g. from the
  // email-first /login screen), so the user only enters their password.
  if (loginHint) params.set("login_hint", loginHint);
  window.location.assign(`${DOMAIN}/oauth2/authorize?${params}`);
}

export async function logout(): Promise<void> {
  if(getSupportStaffViewToken()){clearSupportStaffView();window.location.assign('https://admin.billing.future-demand.com/support-portal?section=pilot');return;}
  const magic = isPortalSession();
  // Clear both browser stores before awaiting revocation, including an in-flight refresh.
  clearSession(); clearPortalSession(); markCustomerSession(false);
  try {
    const res = await fetch(`${API_BASE}/public/portal/logout`, { method: "POST", credentials: "include", headers: portalHeaders() });
    if (!res.ok) throw new Error("logout failed");
  } catch {
    window.location.assign("/login?logout_error=1");
    return;
  }
  if (magic || !DOMAIN || !CLIENT_ID) { window.location.assign("/login"); return; }
  const params = new URLSearchParams({ client_id: CLIENT_ID, logout_uri: LOGOUT_REDIRECT });
  window.location.assign(`${DOMAIN}/logout?${params}`);
}

export async function handleCallback(): Promise<{ returnTo: string }> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code) {
    // Cognito redirected back without a code — surface its actual reason instead of a
    // generic "missing code" (e.g. unauthorized_client = auth-code grant not enabled,
    // invalid_scope = scopes not enabled on the client, redirect_mismatch, etc.).
    const err = url.searchParams.get("error");
    const desc = url.searchParams.get("error_description");
    throw new Error(desc || err || "no authorization code returned by Cognito");
  }
  const pkce = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? "{}") as {
    verifier?: string;
    state?: string;
    returnTo?: string;
  };
  if (!pkce.verifier || pkce.state !== state) throw new Error("pkce state mismatch");
  sessionStorage.removeItem(PKCE_KEY);
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT,
    code_verifier: pkce.verifier,
  });
  const res = await fetch(`${DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`token exchange failed (${res.status}): ${await res.text()}`);
  const data = (await res.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  writeSession({
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + data.expires_in * 1000 - 30_000,
  });
  return { returnTo: pkce.returnTo ?? "/app" };
}

export async function getAccessToken(): Promise<string | null> {
  const s = readSession();
  if (!s) return null;
  if (Date.now() < s.expires_at) return s.access_token;
  if (!s.refresh_token) {
    clearSession();
    return null;
  }
  const stillCurrent = () => readSession()?.refresh_token === s.refresh_token;
  try {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      refresh_token: s.refresh_token,
    });
    const res = await fetch(`${DOMAIN}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      if (stillCurrent()) clearSession();
      return null;
    }
    const data = (await res.json()) as { access_token: string; id_token: string; expires_in: number };
    // A refresh started before logout/account switching must not restore the
    // previous account's credentials after they have been cleared or replaced.
    if (!stillCurrent()) return null;
    writeSession({
      ...s,
      access_token: data.access_token,
      id_token: data.id_token,
      expires_at: Date.now() + data.expires_in * 1000 - 30_000,
    });
    return data.access_token;
  } catch {
    if (stillCurrent()) clearSession();
    return null;
  }
}

export function isAuthenticated(): boolean {
  // An admin read-only "view as customer" grant counts as authenticated — the app
  // shell renders the portal and apiFetch carries the grant instead of a Cognito token.
  if (isAdminView() || getSupportStaffViewToken() || hasCustomerSession()) return true;
  // A passwordless portal session (email magic-link login) also counts — the httpOnly
  // fd_portal_session cookie rides along on apiFetch; /me validates it server-side.
  if (isPortalSession()) return true;
  const s = readSession();
  return !!s && (Date.now() < s.expires_at || !!s.refresh_token);
}

/** True once the Cognito build vars are present — avoids a broken sign-in redirect. */
export function isConfigured(): boolean {
  // In admin-view or portal-session mode there's no Cognito sign-in to configure; treat
  // as configured so AppShell renders instead of the "sign-in isn't configured" banner.
  if (isAdminView() || getSupportStaffViewToken() || isPortalSession() || hasCustomerSession()) return true;
  return Boolean(DOMAIN && CLIENT_ID);
}

function randomString(n: number): string {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return base64url(bytes).slice(0, n);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(hash));
}

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
